import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import TipTapEditor from "components/editor/TipTapEditor";
import React, { useCallback, useEffect, useRef } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { KbDetailsForm } from "./components/KbDetailsForm";
import {
  useCreateSnapshotFromRecord,
  useDeleteKbRecords,
  useGetAutosavedKbFile,
  useGetKbFileDetails,
  useGetLiveKbFileDetails,
  usePermissionStore,
  usePublishKbFileSnapshot,
  useRestoreOldVersion,
  useSaveSnapshotToKbFile,
  useUnpublishKbFile,
  useUpdateKbFile,
  useUpdateKbFileName,
  useUpdateViewedAt,
} from "controller";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { KbFileDetailResponse, PERMISSIONS } from "shared";
import {
  Button,
  CircularLoader,
  EditIcon,
  Error404Component,
  InfoIcon,
  Tab,
  Tabs,
} from "components";
import toast from "react-hot-toast";
import { YjsCollaborationProvider } from "pages/modules/kb/helpers/YjsCollaborationContext";
import { KbCollaborativeEditor } from "./components/KbCollaborativeEditor";
import { ActiveUsersList } from "./components/ActiveUsersList";
import { useGlobalStore } from "controller/store/globalStore";
import { ActionButtonType, BannerButtonType } from "./types";
import { ConfirmationModal } from "components/modal/ConfirmationModal";
import { KbDiscardButton } from "./components/KbDiscardButton";
import { KbBanner } from "./components/KbBanner";
import { KbFormBottomButtons } from "./components/KbFormBottomButtons";
import {
  generateUserColor,
  setFirstH1AsFilename,
  tiptapJsonToYjsUpdate,
} from "./helpers";
import { useAtomValue, useSetAtom } from "jotai";
import {
  hasPublishedVersions,
  isPublished,
  setIsPublished,
} from "./store/kbStore";
import { KbShareButton } from "./components/KbShareButton";
import { addMixpanelEvent, useMixpanelPageViewTracker } from "utilz/helpers";

const schema = yup.object({
  name: yup.string().required("Name is required").trim(),
  type: yup.string().required("Type is required").trim(),
  content: yup.object().required("Content is required"),
  raw_text: yup.string(),
  parentId: yup.string().nullable(),
});

type KbFormData = yup.InferType<typeof schema>;

// Reusable confirmation modal types
type ConfirmationModalConfig = {
  open: boolean;
  title?: string;
  tagline: string;
  primaryButtonText: string;
  secondaryButtonText: string;
  onPrimaryButtonClick: () => void | Promise<void>;
  onSecondaryButtonClick?: () => void;
};

const initialModalConfig: ConfirmationModalConfig = {
  open: false,
  title: "Delete confirmation",
  tagline: "",
  primaryButtonText: "Confirm",
  secondaryButtonText: "Cancel",
  onPrimaryButtonClick: () => {},
  onSecondaryButtonClick: undefined,
};

interface KbDetailScreenProps {
  fileId?: string;
  versionId?: string;
  snapshotId?: string;
  mode?: "edit" | "view";
  isDrawer?: boolean;
  onClose?: () => void;
  onParamsChange?: (params: {
    versionId?: string | null;
    snapshotId?: string | null;
    mode?: string | null;
  }) => void;
}

const KbDetailScreen = ({
  fileId: propFileId,
  versionId: propVersionId,
  snapshotId: propSnapshotId,
  mode: propMode,
  isDrawer = false,
  onClose,
  onParamsChange,
}: KbDetailScreenProps = {}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const haveFullAccess = usePermissionStore((state) =>
    state.hasPermissions([PERMISSIONS.KB.KB_KB_RW]),
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentFileData, setCurrentFileData] =
    React.useState<KbFileDetailResponse | null>(null);
  const [tabValue, setTabValue] = React.useState(0);
  const [confirmationModal, setConfirmationModal] =
    React.useState<ConfirmationModalConfig>(initialModalConfig);

  const setIsPublishedAtom = useSetAtom(setIsPublished);

  const { fileId: routeFileId } = useParams();
  const [isEditable, setIsEditable] = React.useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const filenameDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Drawer mode: internal state for params
  const [drawerParams, setDrawerParams] = React.useState({
    versionId: propVersionId,
    snapshotId: propSnapshotId,
    mode: propMode,
  });

  // Sync props to state when they change (for drawer mode)
  useEffect(() => {
    if (isDrawer) {
      setDrawerParams({
        versionId: propVersionId,
        snapshotId: propSnapshotId,
        mode: propMode,
      });
    }
  }, [isDrawer, propVersionId, propSnapshotId, propMode]);

  // Resolved params: use props/drawer state if in drawer, else URL params
  const resolvedFileId = propFileId || routeFileId;

  useMixpanelPageViewTracker({
    event: "kb_article_page_visited",
    data: {
      articleId: resolvedFileId,
    },
  });
  const resolvedVersionId = isDrawer
    ? drawerParams.versionId
    : searchParams.get("v_id");
  const resolvedSnapshotId = isDrawer
    ? drawerParams.snapshotId
    : searchParams.get("ss_id");
  const resolvedMode = isDrawer ? drawerParams.mode : searchParams.get("mode");

  // Unified param update function
  const updateParams = useCallback(
    (newParams: {
      versionId?: string | null;
      snapshotId?: string | null;
      mode?: string | null;
    }) => {
      if (isDrawer) {
        setDrawerParams((prev) => ({
          versionId:
            newParams.versionId !== undefined
              ? (newParams.versionId ?? undefined)
              : prev.versionId,
          snapshotId:
            newParams.snapshotId !== undefined
              ? (newParams.snapshotId ?? undefined)
              : prev.snapshotId,
          mode:
            newParams.mode !== undefined
              ? ((newParams.mode as "edit" | "view" | undefined) ?? undefined)
              : prev.mode,
        }));
        onParamsChange?.(newParams);
      } else {
        const params = new URLSearchParams(searchParams);
        if (newParams.versionId !== undefined) {
          newParams.versionId
            ? params.set("v_id", newParams.versionId)
            : params.delete("v_id");
        }
        if (newParams.snapshotId !== undefined) {
          newParams.snapshotId
            ? params.set("ss_id", newParams.snapshotId)
            : params.delete("ss_id");
        }
        if (newParams.mode !== undefined) {
          newParams.mode
            ? params.set("mode", newParams.mode)
            : params.delete("mode");
        }
        setSearchParams(params);
      }
    },
    [isDrawer, searchParams, setSearchParams, onParamsChange],
  );

  // Extract user info for collaboration
  const userId = useGlobalStore((state) => state.resourceId);
  const userName = useGlobalStore((state) => state.name);
  const userAvatar = useGlobalStore((state) => state.profilePictureUrl);
  const mspId = useGlobalStore((state) => state.mspId);

  const {
    mutateAsync: createSnapshotFromRecord,
    isLoading: isCreateFromSnapshotLoading,
  } = useCreateSnapshotFromRecord();
  const { data: kbFileDetails, isFetching: isFileDetailsLoading } =
    useGetLiveKbFileDetails(resolvedFileId as string, !resolvedVersionId);

  const { data: kbVersionData, isFetching: isKbVersionDataLoading } =
    useGetKbFileDetails(resolvedVersionId as string);

  const { mutateAsync: updateKbFile, isLoading: isFileUpdating } =
    useUpdateKbFile();

  const { mutateAsync: updateKbFileName, isLoading: isFileNameUpdating } =
    useUpdateKbFileName();

  const { mutateAsync: saveSnapshotToKbFile, isLoading: isSnapshotSaving } =
    useSaveSnapshotToKbFile();

  const { mutateAsync: restoreOldVersion, isLoading: isRestoring } =
    useRestoreOldVersion();
  const { mutateAsync: deleteKbRecords, isLoading: isDeletingKbRecords } =
    useDeleteKbRecords();

  const { mutateAsync: unpublishKbFile, isLoading: isUnpublishing } =
    useUnpublishKbFile();

  const { mutateAsync: publishSnapshot } = usePublishKbFileSnapshot();

  const { data: updateViewedAtData } = useUpdateViewedAt(
    kbFileDetails?.mapping_id as string,
  );

  useEffect(() => {
    if (resolvedVersionId && kbVersionData) {
      setCurrentFileData(kbVersionData);
      setIsPublishedAtom(kbVersionData.versions);
    } else if (kbFileDetails) {
      setCurrentFileData(kbFileDetails);
      setIsPublishedAtom(kbFileDetails.versions);
    }

    return () => {
      setCurrentFileData(null);
      setIsPublishedAtom(undefined);
    };
  }, [kbVersionData, kbFileDetails, resolvedVersionId]);

  const methods = useForm<KbFormData>({
    resolver: yupResolver(schema),
    values: currentFileData
      ? {
          name: currentFileData.name,
          type: currentFileData.record_visibility,
          content: currentFileData.content,
          raw_text: currentFileData.raw_text || "",
          parentId: currentFileData.parent_id,
        }
      : undefined,
    defaultValues: {
      name: "",
      type: "draft",
      content: {},
      raw_text: "",
      parentId: null,
    },
  });
  const { data: autosavedKbFile, isLoading: isFetchingSnapshot } =
    useGetAutosavedKbFile(kbFileDetails?.article_id as string);
  const handleSave = async (shouldExit: boolean = true) => {
    const data = methods.getValues();
    const isValid = await methods.trigger();
    if (!isValid) {
      console.log(methods.formState.errors);
      return;
    }

    try {
      const snapshotIdToUse =
        resolvedSnapshotId ||
        (autosavedKbFile ? autosavedKbFile?.mapping_id : undefined) ||
        kbFileDetails?.mapping_id ||
        "";

      if (!data.parentId) {
        toast.error("Parent ID is required");
        return;
      }
      const fileData = await saveSnapshotToKbFile({
        articleId: resolvedFileId as string,
        snapshotId: snapshotIdToUse,
        fileData: {
          name: data.name,
          content_type: "JSON",
          parent_id: data.parentId || null,
          content: data.content,
          raw_text: data.raw_text || "",
          excerpt: data.raw_text || "",
          record_visibility: data.type as "draft" | "public" | "private",
        },
      });

      toast.success("Article saved successfully");
      addMixpanelEvent({
        event:
          data.type === "draft"
            ? "kb_article_saved_as_draft"
            : "kb_article_published",
        data: {
          articleId: resolvedFileId,
          folderId: data.parentId,
        },
      });

      if (data.type === "draft" && !shouldExit) {
        updateParams({ versionId: fileData[0]?.mapping_id, mode: null });
      } else if (data.type === "public") {
        updateParams({ versionId: null, snapshotId: null, mode: null });
        if (isDrawer) {
          onClose?.();
        }
      }
      if (isDrawer) {
        onClose?.();
      }
      if (shouldExit) {
        navigate(`/app/kb/folder/${data.parentId}`);
      }
    } catch (error) {
      toast.error("Failed to save article");
    }
  };

  const onSubmit = methods.handleSubmit(
    async () => {
      await handleSave(false);
    },
    (err) => {
      console.log(err);
    },
  );

  useEffect(() => {
    setIsEditable(resolvedMode === "edit");
  }, [resolvedMode]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (filenameDebounceRef.current) {
        clearTimeout(filenameDebounceRef.current);
      }
    };
  }, []);

  const handleRestoreVersion = async () => {
    try {
      if (resolvedVersionId) {
        await restoreOldVersion(resolvedVersionId as string);
        toast.success("Version restored successfully");
      }
    } catch (error) {
      console.error("Error restoring KB version:", error);
      toast.error("Failed to restore version");
    }
  };

  const handleDeleteVersion = async () => {
    try {
      if (currentFileData?.is_live === true) {
        toast.error("Cannot delete a live version");
        return;
      }
      if (resolvedMode === "edit") {
        toast.error("Cannot delete a version in edit mode");
        return;
      }
      // if draft
      // revert draft to last published doc
      // mark snapshot as publish so that draft is not displayed in Version list
      if (
        currentFileData?.record_visibility === "draft" &&
        currentFileData.version === 0
      ) {
        const liveVersion = kbFileDetails?.versions?.find(
          (item) => item.is_live,
        );
        await createSnapshotFromRecord(liveVersion?.mapping_id || "");

        await publishSnapshot(currentFileData?.article_id);
      } else {
        await deleteKbRecords([currentFileData?.mapping_id as string]);
      }

      // navigate back to latest version
      // find latest version from kbFileDetails.versions where is_live is latest
      // navigate to that
      navigateToLiveVersion();
      closeConfirmationModal();
      toast.success("Version deleted successfully");
    } catch (error) {
      toast.error("Failed to delete version");
    }
  };

  // Reusable modal helper functions
  const openConfirmationModal = (
    config: Omit<ConfirmationModalConfig, "open">,
  ) => {
    setConfirmationModal({
      ...config,
      open: true,
      onSecondaryButtonClick:
        config.onSecondaryButtonClick || closeConfirmationModal,
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(initialModalConfig);
  };

  const navigateToLiveVersion = (newTab?: boolean) => {
    const latestVersion = kbFileDetails?.versions?.find(
      (v) => v.is_live === true,
    );
    if (latestVersion) {
      if (isDrawer) {
        // In drawer, reset to live version state
        updateParams({ versionId: null, snapshotId: null, mode: null });
      } else {
        if (newTab) {
          window.open(`/app/kb/document/${latestVersion.article_id}`, "_blank");
        } else {
          navigate(`/app/kb/document/${latestVersion.article_id}`);
        }
      }
    }
  };

  const handleDiscardChanges = async () => {
    try {
      const parentId = kbFileDetails?.parent_id;
      methods.reset();
      await deleteKbRecords([kbFileDetails?.mapping_id as string]);
      if (isDrawer) {
        onClose?.();
      } else {
        navigate(`/app/kb/folder/${parentId}`);
      }
    } catch (error) {
      toast.error("Failed to discard changes");
    }
  };

  const handleEditSnapshot = async () => {
    try {
      updateParams({
        mode: "edit",
        snapshotId: currentFileData?.article_id || "",
      });
    } catch (error) {
      toast.error("Failed to create snapshot");
    }
  };

  const handleUnpublish = async () => {
    try {
      await unpublishKbFile(kbFileDetails?.article_id as string);
      toast.success("Article unpublished successfully");
      addMixpanelEvent({
        event: "kb_article_unpublished",
        data: {
          articleId: kbFileDetails?.article_id,
          folderId: kbFileDetails?.parent_id,
        },
      });
    } catch (error) {
      console.error("Error unpublishing version:", error);
      toast.error("Failed to unpublish article");
    }
  };

  const handleNotSavedStateFileNameUpdate = useCallback(() => {
    if (currentFileData?.record_visibility !== "unsaved") {
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced update
    debounceTimerRef.current = setTimeout(async () => {
      try {
        await updateKbFileName({
          fileId: currentFileData?.mapping_id as string,
          fileData: {
            name: methods.getValues("name"),
            content: methods.getValues("content"),
            record_visibility: "unsaved",
          },
        });
        // toast.success("Article unpublished successfully");
      } catch (error) {
        console.error("Error handleNotSavedStateFileNameUpdate", error);
      }
    }, 2000); // 2 second debounce delay
  }, [currentFileData?.record_visibility, kbFileDetails?.mapping_id]);

  const handleRevertToPublishedVersion = async () => {
    try {
      const publishedLiveVersion = kbFileDetails?.versions?.find(
        (item) => item.record_visibility === "public" && item.is_live,
      );
      await createSnapshotFromRecord(publishedLiveVersion?.mapping_id || "");
      toast.success("Reverted to published version");
      closeConfirmationModal();
      navigate(`/app/kb/document/${currentFileData?.article_id}`);
    } catch (error) {
      console.error("Error reverting to published version:", error);
      toast.error("Failed to revert to published version");
    }
  };

  const handleRevertToCurrentVersion = async () => {
    try {
      await createSnapshotFromRecord(currentFileData?.mapping_id || "");
      setTimeout(() => {
        // toast.success("Reverted to current version");
        updateParams({
          mode: "edit",
          snapshotId: currentFileData?.article_id || "",
        });
      }, 2000);
      closeConfirmationModal();
    } catch (error) {
      console.error("Error reverting to current version:", error);
      toast.error("Failed to revert to current version");
    }
  };

  const handleActionButtonClick = (type: ActionButtonType) => {
    switch (type) {
      case "draft":
        methods.setValue("type", "draft");
        onSubmit();
        break;
      case "save_exit":
        methods.setValue("type", "draft");
        handleSave(true);
        break;
      case "publish":
        methods.setValue("type", "public");
        onSubmit();
        break;
      case "restore":
        handleRestoreVersion();
        break;

      case "delete_version":
        openConfirmationModal({
          tagline:
            "Are you sure you want to delete this version? This action cannot be undone.",
          primaryButtonText: "Delete",
          secondaryButtonText: "Cancel",
          onPrimaryButtonClick: handleDeleteVersion,
        });
        break;

      case "edit":
        // setSearchParams({ mode: "edit" });
        handleEditSnapshot();
        break;
      case "discard":
        handleDiscardChanges();
        break;

      case "unpublish":
        // Handle unpublish logic here
        handleUnpublish();
        break;
    }
  };

  const handleBannerButtonClick = (type: BannerButtonType) => {
    switch (type) {
      case "view_published":
        navigateToLiveVersion();
        break;
      case "view_published_new_tab":
        navigateToLiveVersion(true);
        break;
      case "view_draft":
        handleEditSnapshot();
        break;
      case "restore_live_version":
        openConfirmationModal({
          title: "Discard changes",
          tagline:
            "Are you sure you want to discard these changes? This will revert to the last published version.",
          primaryButtonText: "Discard",
          secondaryButtonText: "Cancel",
          onPrimaryButtonClick: handleRevertToPublishedVersion,
        });
        break;
      default:
        break;
    }
  };

  const hasPublishedVersionsData = useAtomValue(hasPublishedVersions);
  const hasUnsavedVersion = autosavedKbFile && !autosavedKbFile.is_published;
  const isEditMode = resolvedMode === "edit";

  if (isFileDetailsLoading || isKbVersionDataLoading) {
    return (
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularLoader />
      </Stack>
    );
  }

  if (
    !kbFileDetails &&
    !isFileDetailsLoading &&
    !kbVersionData &&
    !isKbVersionDataLoading
  ) {
    return (
      <Error404Component
        title="Article not found"
        subtitle="The article you're looking for doesn't exist or has been removed."
        showCta={false}
      />
    );
  }
  return (
    <FormProvider {...methods}>
      <form style={{ height: "100%" }}>
        <Stack
          sx={{
            width: "100%",
            height: isDrawer ? "100%" : "calc(100vh - 64px)",
            p: "8px",
            display: "grid",
            gridTemplateColumns: "1fr 360px",
            columnGap: "8px",
            bgcolor: theme.palette.neutral["010"],
            overflow: "hidden",
          }}
        >
          <Stack
            sx={{
              bgcolor: "#FFF",
              height: "100%",
              borderRadius: "8px",
              overflow: "hidden",
              // pb: "84px",
            }}
          >
            {/* <Typography variant="body-xsmall">
              {JSON.stringify(autosavedKbFile, null, 2)}
            </Typography> */}
            {/* banner */}
            <KbBanner
              isLoading={isFileDetailsLoading || isKbVersionDataLoading}
              mode={isEditable ? "edit" : "view"}
              kbFileDetails={currentFileData as KbFileDetailResponse}
              tabValue={tabValue}
              onBannerButtonClick={handleBannerButtonClick}
              snapshot={autosavedKbFile}
              haveFullPermission={haveFullAccess}
            />

            {!isFileDetailsLoading &&
              !isKbVersionDataLoading &&
              !isFetchingSnapshot &&
              currentFileData &&
              userId &&
              mspId && (
                <YjsCollaborationProvider
                  kbRecordId={kbFileDetails?.mapping_id as string}
                  snapshotId={resolvedSnapshotId || ""}
                  articleId={kbFileDetails?.article_id as string}
                  userId={userId}
                  userName={userName || ""}
                  mspId={mspId}
                  isEditable={isEditable && haveFullAccess}
                  userColor={generateUserColor(userId, theme)}
                  userAvatar={userAvatar}
                >
                  {/* Active users list - positioned top-right */}

                  {/* Offline indicator - positioned top-center */}
                  {/* {isEditable && <OfflineIndicator />} */}

                  <Stack
                    sx={{
                      p: "40px",
                      position: "relative",
                      overflow: "auto",
                      height: "100%",
                    }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "space-between",
                        alignContent: "center",
                        pl: "16px",
                        // height: "88px",
                      }}
                    >
                      <Box>
                        {isEditable && haveFullAccess && <ActiveUsersList />}
                      </Box>
                      <Stack direction={"row"} gap="8px" alignItems={"center"}>
                        {!isEditable && haveFullAccess && (
                          <Stack
                            // component={"button"}
                            onClick={() => {
                              if (
                                (!hasUnsavedVersion &&
                                  currentFileData.is_live) ||
                                currentFileData.record_visibility !== "public"
                              ) {
                                handleEditSnapshot();
                              } else if (!hasUnsavedVersion) {
                                handleRevertToCurrentVersion();
                                return;
                              } else {
                                openConfirmationModal({
                                  title: "Draft already in use",
                                  tagline:
                                    "This article already has an active draft. Continuing will replace the existing draft and discard any unsaved changes.",
                                  primaryButtonText: "Replace draft",
                                  secondaryButtonText: "Cancel",
                                  onPrimaryButtonClick:
                                    handleRevertToCurrentVersion,
                                });
                              }
                            }}
                            sx={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "100%",
                              boxShadow:
                                "0px 0px 4px 0px #00000029,0px 0px 2px 0px #00000014",
                              border: `1px solid ${theme.palette.neutral["010"]}`,
                              justifyContent: "center",
                              alignItems: "center",
                              bgcolor: "#FFF",
                              cursor: "pointer",
                            }}
                          >
                            {isCreateFromSnapshotLoading ? (
                              <CircularLoader size={16} />
                            ) : (
                              <EditIcon />
                            )}
                          </Stack>
                        )}
                        {!isEditable &&
                          kbFileDetails?.record_visibility === "public" &&
                          kbFileDetails?.is_live && (
                            <KbShareButton
                              mode={isEditable ? "edit" : "view"}
                              kbFileDetails={
                                kbFileDetails as KbFileDetailResponse
                              }
                            />
                          )}

                        <KbDiscardButton
                          mode={isEditable ? "edit" : "view"}
                          kbFileDetails={kbFileDetails as KbFileDetailResponse}
                          tabValue={tabValue}
                          onActionButtonClick={handleActionButtonClick}
                          isDrawer={isDrawer}
                        />
                      </Stack>
                    </Stack>
                    {/* <Stack direction={"row"} sx={{}}>
                      {isEditable && <ActiveUsersList />}

                      <KbDiscardButton
                        mode={isEditable ? "edit" : "view"}
                        kbFileDetails={kbFileDetails as KbFileDetailResponse}
                        tabValue={tabValue}
                        onActionButtonClick={handleActionButtonClick}
                      />
                    </Stack> */}

                    {/* Collaborative editor */}
                    <Controller
                      name="content"
                      control={methods.control}
                      render={({ field }) => (
                        <KbCollaborativeEditor
                          recordVisibility={currentFileData?.record_visibility}
                          kbRecordId={kbFileDetails?.mapping_id as string}
                          snapshotId={resolvedSnapshotId || ""}
                          initialContent={field.value}
                          isEditable={isEditable}
                          userId={userId}
                          userName={userName || "Anonymous"}
                          mspId={mspId}
                          autosave={true}
                          onSave={(data) => {
                            // handleAutoSave();
                          }}
                          onContentChange={(content) => {
                            if (isEditable) {
                              field.onChange(content?.content);
                              methods.setValue(
                                "raw_text",
                                content?.textContent,
                              );
                              // Debounce filename extraction to avoid running on every keystroke
                              if (content?.content) {
                                if (filenameDebounceRef.current) {
                                  clearTimeout(filenameDebounceRef.current);
                                }
                                filenameDebounceRef.current = setTimeout(
                                  async () => {
                                    const name = await setFirstH1AsFilename({
                                      content: content,
                                      currentValue: methods.getValues("name"),
                                    });
                                    methods.setValue(
                                      "name",
                                      name || "Untitled",
                                    );
                                    handleNotSavedStateFileNameUpdate();
                                  },
                                  500,
                                );
                              }
                            }
                          }}
                        />
                      )}
                    />
                  </Stack>
                </YjsCollaborationProvider>
              )}
          </Stack>
          <Stack
            sx={{
              bgcolor: "#FFF",
              height: isDrawer ? "100%" : "calc(100vh - 82px)",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Stack sx={{ flexShrink: 0 }}>
              <Stack
                sx={{
                  p:
                    hasPublishedVersionsData && !isEditMode
                      ? "12px 24px 0px 24px"
                      : "12px 24px",
                  borderBottom:
                    hasPublishedVersionsData && !isEditMode
                      ? "none"
                      : `1px solid ${theme.palette.neutral["015"]}`,
                }}
              >
                <Typography variant="h5">Details</Typography>
              </Stack>
              {hasPublishedVersionsData && !isEditMode ? (
                <Tabs
                  sx={{
                    borderBottom: `1px solid ${theme.palette.neutral["015"]}`,
                    px: "24px",
                  }}
                  value={tabValue}
                  onChange={(_, value) => setTabValue(value)}
                >
                  <Tab label="General" />
                  <Tab label="Version History" />
                </Tabs>
              ) : (
                <></>
              )}
            </Stack>

            <Stack sx={{ flexGrow: 1, overflowY: "scroll" }}>
              <KbDetailsForm
                mode={isEditable ? "edit" : "view"}
                isFileDetailsLoading={
                  isFileDetailsLoading || isKbVersionDataLoading
                }
                onActionButtonClick={handleActionButtonClick}
                kbFileDetails={currentFileData as KbFileDetailResponse}
                isPrimaryActionLoading={isFileUpdating || isSnapshotSaving}
                isSecondaryActionLoading={false}
                tabValue={tabValue}
                snapshot={autosavedKbFile}
              />
            </Stack>
            <Stack sx={{ flexShrink: 0 }}>
              {haveFullAccess && (
                <KbFormBottomButtons
                  onActionButtonClick={handleActionButtonClick}
                  isPrimaryActionLoading={isFileUpdating || isSnapshotSaving}
                  isSecondaryActionLoading={false}
                  mode={isEditable ? "edit" : "view"}
                  tabValue={tabValue}
                  kbFileDetails={currentFileData as KbFileDetailResponse}
                  isLiveVersion={currentFileData?.is_live || false}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </form>
      {confirmationModal.open && (
        <ConfirmationModal
          isLoading={isCreateFromSnapshotLoading}
          open={confirmationModal.open}
          title={confirmationModal.title}
          tagline={confirmationModal.tagline}
          onPrimaryButtonClick={confirmationModal.onPrimaryButtonClick}
          onSecondaryButtonClick={
            confirmationModal.onSecondaryButtonClick || closeConfirmationModal
          }
          primaryButtonText={confirmationModal.primaryButtonText}
          secondaryButtonText={confirmationModal.secondaryButtonText}
        />
      )}
    </FormProvider>
  );
};

export default KbDetailScreen;
